import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Device, SensorDefinition, LoadingState } from "@types";
import { devicesApi } from "@api/devices";

interface DevicesState {
  devices: Device[];
  selectedDevice: Device | null;
  loadingState: LoadingState;
  error: string | null;
  sensorDefEdited: boolean;
  deletedButNotSavedDevices: boolean;
  pendingDeletions: number[];
  deviceEdited: boolean;
}

const initialState: DevicesState = {
  devices: [],
  selectedDevice: null,
  loadingState: "idle",
  error: null,
  sensorDefEdited: false,
  deletedButNotSavedDevices: false,
  pendingDeletions: [],
  deviceEdited: false,
};

// Async thunks
export const fetchDevices = createAsyncThunk<Device[]>(
  "devices/fetchDevices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await devicesApi.getDevices();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch devices",
      );
    }
  },
);

export const createDevice = createAsyncThunk<Device, Partial<Device>>(
  "devices/createDevice",
  async (deviceData: Partial<Device>, { rejectWithValue }) => {
    try {
      const response = await devicesApi.createDevice(deviceData);
      // Backend returns object directly for create, or wrapped in .data.data
      return (response.data as any).data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create device",
      );
    }
  },
);

export const updateDevice = createAsyncThunk<
  Device,
  { id: number; data: Partial<Device> }
>(
  "devices/updateDevice",
  async (
    { id, data }: { id: number; data: Partial<Device> },
    { rejectWithValue },
  ) => {
    try {
      const response = await devicesApi.updateDevice(id, data);
      return (response.data as any).data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update device",
      );
    }
  },
);

export const deleteDevice = createAsyncThunk<number, number>(
  "devices/deleteDevice",
  async (id: number, { rejectWithValue }) => {
    try {
      await devicesApi.deleteDevice(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete device",
      );
    }
  },
);

export const batchSaveDevices = createAsyncThunk<void, { deletions: number[] }>(
  "devices/batchSaveDevices",
  async ({ deletions }, { rejectWithValue }) => {
    try {
      // Delete all marked devices
      await Promise.all(deletions.map((id) => devicesApi.deleteDevice(id)));
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save changes",
      );
    }
  },
);

export const updateSensorDefinition = createAsyncThunk(
  "devices/updateSensorDefinition",
  async (
    {
      deviceId,
      sensorDefId,
      data,
    }: {
      deviceId: number;
      sensorDefId: number;
      data: Partial<SensorDefinition>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await devicesApi.updateSensorDefinition(
        deviceId,
        sensorDefId,
        data,
      );
      const sensorData = (response.data as any).data || response.data;
      return { deviceId, sensorDef: sensorData };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update sensor definition",
      );
    }
  },
);

const devicesSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {
    setSelectedDevice: (state, action: PayloadAction<Device | null>) => {
      state.selectedDevice = action.payload;
    },
    setSensorDefEdited: (state, action: PayloadAction<boolean>) => {
      state.sensorDefEdited = action.payload;
    },
    setDeletedButNotSavedDevices: (state, action: PayloadAction<boolean>) => {
      state.deletedButNotSavedDevices = action.payload;
    },
    setDeviceEdited: (state, action: PayloadAction<boolean>) => {
      state.deviceEdited = action.payload;
    },
    markForDeletion: (state, action: PayloadAction<number>) => {
      if (!state.pendingDeletions.includes(action.payload)) {
        state.pendingDeletions.push(action.payload);
        state.deletedButNotSavedDevices = true;
      }
    },
    unmarkForDeletion: (state, action: PayloadAction<number>) => {
      state.pendingDeletions = state.pendingDeletions.filter(
        (id) => id !== action.payload,
      );
      state.deletedButNotSavedDevices = state.pendingDeletions.length > 0;
    },
    clearPendingDeletions: (state) => {
      state.pendingDeletions = [];
      state.deletedButNotSavedDevices = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Devices
      .addCase(fetchDevices.pending, (state) => {
        state.loadingState = "pending";
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loadingState = "succeeded";
        state.devices = action.payload;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loadingState = "failed";
        state.error = action.payload as string;
      })

      // Create Device
      .addCase(createDevice.pending, (state) => {
        state.error = null;
      })
      .addCase(createDevice.fulfilled, (state, action) => {
        state.devices.push(action.payload);
      })
      .addCase(createDevice.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Update Device
      .addCase(updateDevice.pending, (state) => {
        state.error = null;
      })
      .addCase(updateDevice.fulfilled, (state, action) => {
        const index = state.devices.findIndex(
          (device) => device.id === action.payload.id,
        );
        if (index !== -1) {
          state.devices[index] = action.payload;
        }
        if (state.selectedDevice?.id === action.payload.id) {
          state.selectedDevice = action.payload;
        }
      })
      .addCase(updateDevice.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Delete Device
      .addCase(deleteDevice.fulfilled, (state, action) => {
        state.devices = state.devices.filter(
          (device) => device.id !== action.payload,
        );
        if (state.selectedDevice?.id === action.payload) {
          state.selectedDevice = null;
        }
        state.pendingDeletions = state.pendingDeletions.filter(
          (id) => id !== action.payload,
        );
      })

      // Batch Save Devices
      .addCase(batchSaveDevices.pending, (state) => {
        state.loadingState = "pending";
      })
      .addCase(batchSaveDevices.fulfilled, (state) => {
        state.loadingState = "succeeded";
        state.devices = state.devices.filter(
          (device) => !state.pendingDeletions.includes(device.id),
        );
        state.pendingDeletions = [];
        state.deletedButNotSavedDevices = false;
        state.deviceEdited = false;
      })
      .addCase(batchSaveDevices.rejected, (state, action) => {
        state.loadingState = "failed";
        state.error = action.payload as string;
      })

      // Update Sensor Definition
      .addCase(updateSensorDefinition.fulfilled, (state, action) => {
        const { deviceId, sensorDef } = action.payload;
        const device = state.devices.find((d) => d.id === deviceId);
        if (device) {
          const sensorDefIndex = device.sensor_definitions.findIndex(
            (sd) => sd.id === sensorDef.id,
          );
          if (sensorDefIndex !== -1) {
            device.sensor_definitions[sensorDefIndex] = sensorDef;
          }
        }
        if (state.selectedDevice?.id === deviceId) {
          const sensorDefIndex =
            state.selectedDevice.sensor_definitions.findIndex(
              (sd) => sd.id === sensorDef.id,
            );
          if (sensorDefIndex !== -1) {
            state.selectedDevice.sensor_definitions[sensorDefIndex] = sensorDef;
          }
        }
        state.sensorDefEdited = false;
      });
  },
});

export const {
  setSelectedDevice,
  setSensorDefEdited,
  setDeletedButNotSavedDevices,
  setDeviceEdited,
  markForDeletion,
  unmarkForDeletion,
  clearPendingDeletions,
  clearError,
} = devicesSlice.actions;

export default devicesSlice.reducer;
